import { Box, Heading, Link, Flex, Button, List, ListItem, ListIcon } from "@chakra-ui/react";
import LandingContent from "../../../components/landing-content";
import { StdLink } from "../../../components/StdLink";
import { H3, LB } from "../../../components/text-formatting";
import { siteConfig } from "../../../configuration/config";
import { contactEmail } from "../terms";
import { MdCheckCircle } from "react-icons/md";

export default function Gdpr() {
  return (
    <LandingContent>
      <Heading mb={3} fontSize={"xl"}>
        {siteConfig.serviceName} Privacy Policy
      </Heading>

      {/* Navigation Links */}
      <Flex mb={6} mt={4}>
        <Link href="#individual-privacy" mr={6}>
          <Button size="sm" variant="outline">
            Individual Users Privacy
          </Button>
        </Link>
        <Link href="#business-privacy">
          <Button size="sm" variant="outline">
            Business & Schools Privacy
          </Button>
        </Link>
      </Flex>

      <div>
        Last Updated: June 13th, 2025
        <LB />
        At {siteConfig.serviceName}, we take your privacy seriously and are committed to protecting
        your personal data in accordance with the General Data Protection Regulation (GDPR). This
        Privacy Policy outlines how we collect, use, and store your personal data when using{" "}
        {siteConfig.serviceName}, a language learning application.
        <Box id="individual-privacy" pt={4}>
          <Heading as="h2" size="md" mb={4}>
            Privacy Policy for Individual Users
          </Heading>
          <H3 text="Collection of Personal Data" />
          When using {siteConfig.serviceName}, we collect the following personal data:
          <List spacing={2} mt={4}>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              User conversations: We save your conversations with our artificial tutors to enhance
              your learning experience and to allow you to review past conversations.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              User settings: We save your settings in the Application to provide a personalized
              experience.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Cookies: We use cookies for authentication and analytics purposes. Authentication
              cookies are used to identify you when you log in, while non-identifiable analytics
              cookies are used to analyze and optimize the {"Application's"} performance.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Voice data: Your voice data is sent to third parties for processing to enable the
              voice-based features of {siteConfig.serviceName} (see sub-processor list below).
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Audio Recordings: When you use our speech features, you can choose to record your
              voice. These recordings are stored so you can play them back and track your
              pronunciation progress over time. Recordings are linked to your account and are only
              accessible to you.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Account email and password: We save your email address and password as part of the
              sign up process. This data is saved with identity provide{" "}
              <StdLink
                href="https://auth0.com/docs/secure/data-privacy-and-compliance"
                text="Auth0"
                target="_blank"
                rel="nofollow noreferrer"
              />{" "}
              in the European Union.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Payment data: if you signed up via our payment gateway, Stripe, then your payment data
              is handled and stored by{" "}
              <StdLink
                href="https://stripe.com/en-dk/legal/ssa"
                text="Stripe Payments Europe"
                rel="nofollow noreferrer"
                target="_blank"
              />
            </ListItem>
          </List>
          <H3 text="Individual User Consent" />
          When using {siteConfig.serviceName} you consent to the collection and processing of your
          voice transcriptions and text input, and that we sent your data to Microsoft and the AI
          providers mentioned above for processing.
          <LB />
          When using {siteConfig.serviceName} you also consent to abstain from sharing any sensitive
          or personal information with our artificial language tutors.
        </Box>
        <Box id="business-privacy" pt={8} mt={4}>
          <Heading as="h2" size="md" mb={4}>
            Privacy Policy for Businesses and Schools
          </Heading>
          <H3 text="Organizational Data Collection" />
          For business and school accounts, we collect additional organizational data including:
          <List spacing={2} mt={4}>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Organization name and contact information
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Administrator account details
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              User license allocation and usage statistics
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Billing information for organizational accounts
            </ListItem>
          </List>
          <H3 text="Student/Employee Data" />
          Organizations are responsible for obtaining appropriate consent from their users (students
          or employees) for the collection and processing of personal data in accordance with
          applicable laws. We process this data as described in the Individual Users section.
          <H3 text="Administrator Access" />
          Administrators of business and school accounts have access to usage statistics and
          learning progress data for their users. This data is provided to help organizations
          monitor engagement and effectiveness. By default, administrators do not have access to the
          specific content of conversations between users and AI tutors unless explicitly permitted
          in your organization&apos;s settings.
          <H3 text="Data Export and Retention" />
          Organizations may request exports of their users&apos; learning progress data. When a user
          is removed from an organization, their data may be retained for a limited period before
          being permanently deleted unless the organization specifically requests immediate
          deletion.
          <H3 text="Organizational Responsibilities" />
          Organizations using {siteConfig.serviceName} are responsible for:
          <List spacing={2} mt={4}>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Obtaining appropriate consent from users for data collection and processing
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Informing users about what data is collected and how it is used
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Ensuring compliance with applicable data protection laws
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Managing user access and permissions appropriately
            </ListItem>
          </List>
        </Box>
        <Box pt={8}>
          <Heading as="h2" size="md" mb={4}>
            General Privacy Information
          </Heading>
          <H3 text="Data Storage and Processing" />
          Artificial Intelligence (AI) models are used to process your voice and text input. We use
          the following model providers for tasks such as response generation, language
          understanding, language generation, and voice recognition:{" "}
          <Link
            href="https://openai.com/policies/privacy-policy"
            target="_blank"
            rel="noreferrer"
            color={"brand.secondary.main"}
          >
            OpenAI
          </Link>
          ,{" "}
          <Link
            href="https://www.anthropic.com/legal/privacy"
            target="_blank"
            rel="noreferrer"
            color={"brand.secondary.main"}
          >
            Anthropic
          </Link>{" "}
          and{" "}
          <Link
            href="https://wow.groq.com/privacy-policy/"
            target="_blank"
            rel="noreferrer"
            color={"brand.secondary.main"}
          >
            Groq
          </Link>
          . Please refer to their privacy policies for more information.
          <LB />
          For European users, your personal data, including conversations and voice data, is stored
          within the European Union at data centers operated by Microsoft, Hetzner, Stripe Payments
          Europe and Auth0. Furthermore, conversations are shared with the AI providers mentioned
          above. For non-European users, your voice data is processed on servers close to your
          location to ensure low latency, while other data is stored in the European Union.
          <H3 text="Sub-processors" />
          We use the following sub-processors to store and process your personal data in accordance
          with the GDPR:
          <List spacing={2} mt={4}>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Microsoft Azure North Europe, Takeda Ireland Ltd (Grange Castle) New Nangor Road,
              Grange Dublin 22, Co. Dublin Ireland.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Microsoft Azure, West Europe, Evert van de Beekstraat 354, 1118 CZ Luchthaven
              Schiphol, Noord-Holland, Netherlands.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              OpenAI Ireland Ltd. 1st Floor, The Liffey Trust Centre, 117-126 Sheriff Street Upper,
              Dublin 1, D01 YC43, Ireland.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Anthropic Ireland Limited, 6th Floor South Bank House, Barrow Street, Dublin 4,
              Ireland.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Groq, Inc. 301 Castro St., Suite 200 Mountain View, CA 94041, USA.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen, Germany, Web hosting.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Auth0, Frankfurt, Germany, Authentication.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Sentry, Frankfurt, Germany, Error logging.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Mixpanel, Eemshaven, Netherlands, Usage tracking.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheckCircle} color="green.500" />
              Cloudflare Germany GmbH, Rosental 7, c/o Mindspace, 80331 München, Germany. CDN, DNS,
              security services.
            </ListItem>
          </List>
          <H3 text="Right to Be Forgotten" />
          You have the right to request the deletion of your personal data from our systems. To
          exercise this right, please send an email to {contactEmail} with the subject{" "}
          {'"Right to Be Forgotten."'} Please settle any unpaid bills before submitting the request
          to ensure a seamless process. Upon receiving your request, we will delete your personal
          data from our systems within 30 days, as required by the GDPR.
          <H3 text="Data Protection" />
          We implement appropriate technical and organizational measures to ensure the security of
          your personal data and protect it against unauthorized access, disclosure, alteration, or
          destruction.
          <H3 text="Changes to GDPR Information" />
          We reserve the right, at our sole discretion, to modify or update this Privacy Policy at
          any time. By continuing to access or use {siteConfig.serviceName} after such
          modifications, you agree to be bound by the updated Privacy Policy.
          <H3 text="Contact Information" />
          If you have any questions about this Privacy Policy or our data protection practices,
          please contact us at {contactEmail}.
          <LB />
          By using {siteConfig.serviceName}, you acknowledge that you have read, understood, and
          agree to the terms outlined in this Privacy Policy.
        </Box>
      </div>

      <LB />
    </LandingContent>
  );
}
